"use client";

import React, { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import styles from "./index.module.css";
import pathwayStyles from "../variants/Pathway/index.module.css";

type Props = {
  children?: React.ReactNode;
  trigger: string | React.ReactNode;
  value?: string;
  variant?: "default" | "pathway" | "pathway-dark";
  defaultOpen?: boolean;
};

export default function AccordionTextItem({
  children,
  trigger,
  value,
  variant,
  defaultOpen = false,
}: Props) {
  const [isHydrated, setIsHydrated] = useState(false);

  // On client-side hydration, hide content if not defaultOpen
  useEffect(() => {
    setIsHydrated(true);
  }, []);

  let variantStyles;

  switch (variant) {
    case "pathway-dark":
    case "pathway":
      variantStyles = pathwayStyles;
      break;
    default:
      variantStyles = styles;
      break;
  }

  const getValue = () => {
    if (value) {
      return value;
    }
    if (typeof trigger === "string") {
      return trigger;
    }
    return uuidv4();
  };

  // For SSR/SEO: show content by default
  // After hydration: use Radix UI behavior
  if (!isHydrated) {
    return (
      <details open className={`${styles.item} ${variantStyles.item ?? ""}`}>
        <summary
          className={`${styles.trigger} ${variantStyles.trigger ?? ""} cursor-pointer list-none [&::-webkit-details-marker]:hidden`}
        >
          <div className="py-2 text-base md:text-lg">
            {typeof trigger === "string" ? <span>{trigger}</span> : trigger}
          </div>
        </summary>
        <div className={`${styles.content} ${variantStyles.content ?? ""}`}>
          {children}
        </div>
      </details>
    );
  }

  // After hydration: use Radix UI
  return (
    <AccordionItem value={getValue()} className={`${variantStyles.item ?? ""}`}>
      <AccordionTrigger className={`${variantStyles.trigger ?? ""}`}>
        <div className="py-2 text-base md:text-lg">
          {typeof trigger === "string" ? <span>{trigger}</span> : trigger}
        </div>
      </AccordionTrigger>
      <AccordionContent className={`${variantStyles.content ?? ""}`} asChild>
        {children}
      </AccordionContent>
    </AccordionItem>
  );
}
